/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';
import { FormFieldValue, FormValidator } from 'src/core/types/form';
import * as booleanValidators from 'src/core/utils/formValidators/booleanValidators';
import * as fileValidators from 'src/core/utils/formValidators/fileValidators';
import * as numberValidators from 'src/core/utils/formValidators/numberValidators';
import * as stringArrayValidators from 'src/core/utils/formValidators/stringArrayValidators';
import * as stringValidators from 'src/core/utils/formValidators/stringValidators';

const messages = defineMessages({
    errorMessage: {
        id: 'portal.blacklistValidator.errorMessage',
        defaultMessage: 'Invalid characters: You can only use letters (a-z, A-Z), numbers (0-9) and symbols @ # _ . , ^ \\\\ [ ]'
    }
});

/**
 * A factory for creating blacklist validators. A black list validator is one
 * that succeeds validation only if the value given does not contain any of the
 * characters defined in the blacklist.
 *
 * @param params.blacklist A string of the characters to blacklist. The characters should
 * be contiguous with no white spaces between them. The value to validate should not
 * contain any characters in this string.
 *  "<", ">", "*", "%", "&", ":", "\\", "@", "$", "~", "#", "(", ")", "+", "="
 * @param params.errorMessage An error message to display when the input is invalid.
 */
export const createBlacklistValidator = (params?: {
    characterList: string | RegExp;
    mode?: 'exclude' | 'include';
    errorMessage?: ReactIntl.FormattedMessage.MessageDescriptor;
}): FormValidator => (value: FormFieldValue) => {
    let validationResult: boolean;
    const mode = params?.mode ?? 'exclude';
    const characterList = params?.characterList ?? '\\/:?&=*+()%$~!><%';

    if (!characterList || !value) {
        return;
    }

    if (typeof value === 'number') {
        validationResult = numberValidators.createBlacklistValidator({ characterList, mode })(value);
    }
    if (typeof value === 'boolean') {
        validationResult = booleanValidators.createBlacklistValidator({ characterList, mode })(value);
    }
    if (typeof value === 'string') {
        validationResult = stringValidators.createBlacklistValidator({ characterList, mode })(value);
    }
    if (value instanceof File) {
        validationResult = fileValidators.createBlacklistValidator({ characterList, mode })(value);
    }
    if (Array.isArray(value)) {
        validationResult = stringArrayValidators.createBlacklistValidator({ characterList, mode })(value);
    }
    if (!validationResult) {
        return { messageDescriptor: params?.errorMessage || messages.errorMessage };
    }
};
